import { resolveProductImageUrl } from './product-image-url.js';
import {
  buildProductPlaceholderDataUri,
  type ProductPlaceholderInput,
  type ProductPlaceholderTheme,
} from './product-placeholder.js';

export interface ProductImageDisplayInput extends ProductPlaceholderInput {
  url?: string | null;
}

export interface ProductImageDisplayResult {
  src: string;
  placeholderSrc: string;
  onError: (event: Event) => void;
}

/**
 * Apply img onerror fallback without looping if the placeholder itself fails.
 */
export function handleProductImageError(event: Event, fallbackSrc: string): void {
  const target = event.target;
  if (!(target instanceof HTMLImageElement)) {
    return;
  }

  if (target.dataset.placeholderFallback === '1' || target.src === fallbackSrc) {
    target.onerror = null;
    return;
  }

  target.dataset.placeholderFallback = '1';
  target.src = fallbackSrc;
  target.onerror = null;
}

/**
 * Resolve the best img[src] for a product, using a generated placeholder when URL is missing.
 */
export function resolveProductImageSrc(input: ProductImageDisplayInput): string {
  const resolved = resolveProductImageUrl(input.url);
  if (resolved) {
    return resolved;
  }
  return buildProductPlaceholderDataUri(input);
}

/**
 * src + onerror handler bundle for Lit/React/vanilla img tags.
 */
export function getProductImageDisplay(input: ProductImageDisplayInput): ProductImageDisplayResult {
  const placeholderSrc = buildProductPlaceholderDataUri(input);
  const src = resolveProductImageUrl(input.url) || placeholderSrc;

  return {
    src,
    placeholderSrc,
    onError: (event: Event) => handleProductImageError(event, placeholderSrc),
  };
}

export type { ProductPlaceholderTheme };
